/*! @placekit/autocomplete-react v2.1.9 | © placekit.io | MIT license | https://github.com/placekit/autocomplete-react#readme */
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import placekitAutocomplete from '@placekit/autocomplete-js';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

// dequal borrowed from https://github.com/lukeed/dequal/blob/master/src/lite.js
const has = Object.prototype.hasOwnProperty;
function dequal(foo, bar) {
  let ctor;
  let len;
  if (foo === bar) return true;
  if (foo && bar && (ctor = foo.constructor) === bar.constructor) {
    if (ctor === Date) return foo.getTime() === bar.getTime();
    if (ctor === RegExp) return foo.toString() === bar.toString();
    if (ctor === Array) {
      if ((len = foo.length) === bar.length) {
        while (len-- && dequal(foo[len], bar[len]));
      }
      return len === -1;
    }
    if (!ctor || typeof foo === 'object') {
      len = 0;
      // eslint-disable-next-line guard-for-in
      for (ctor in foo) {
        if (has.call(foo, ctor) && ++len && !has.call(bar, ctor)) return false;
        if (!(ctor in bar) || !dequal(foo[ctor], bar[ctor])) return false;
      }
      return Object.keys(bar).length === len;
    }
  }
  return foo !== foo && bar !== bar;
}

// prevent re-renders when options prop is an object literal
function useStableValue(value) {
  const [stableValue, setStableValue] = useState(() => value);
  if (!dequal(stableValue, value)) {
    setStableValue(value);
  }
  return stableValue;
}

const usePlaceKit = (apiKey, options = {}) => {
  // throw error if invalid options
  if (typeof options !== 'object' || Array.isArray(options) || options === null) {
    throw Error('PlaceKit: `options` parameter is invalid, expected an object.');
  }

  // init states
  const stableOptions = useStableValue(options);
  const target = useRef(null);
  const [client, setClient] = useState();
  const [state, setState] = useState({
    dirty: false,
    empty: true,
    freeForm: true,
    geolocation: false,
    countryMode: false
  });

  // mount PlaceKit Autocomplete JS
  useEffect(() => {
    if (!target.current) {
      return;
    }
    const pka = placekitAutocomplete(apiKey, {
      target: target.current
    });
    setClient(pka);
    return () => {
      pka.destroy();
      setClient();
    };
  }, [apiKey, target.current]);

  // run `pka.configure()` when options update
  useEffect(() => {
    if (!client) {
      return;
    }
    const {
      handlers,
      ...opts
    } = stableOptions || {};
    client.on('open', handlers?.onOpen).on('close', handlers?.onClose).on('results', handlers?.onResults).on('pick', handlers?.onPick).on('error', handlers?.onError).on('countryChange', handlers?.onCountryChange).on('dirty', handlers?.onDirty).on('empty', handlers?.onEmpty).on('freeForm', handlers?.onFreeForm).on('geolocation', handlers?.onGeolocation).on('countryMode', handlers?.onCountryMode).on('state', ({
      ...newState
    }) => {
      // spread to remove `client.state` reference
      setState(newState);
      if (handlers?.onState) {
        handlers.onState(newState);
      }
    }).configure(opts);

    // inject initial state from client (spread to remove `client.state` reference)
    setState({
      ...client.state
    });
  }, [client, stableOptions]);
  return {
    target,
    client,
    state
  };
};

const PlaceKit = /*#__PURE__*/forwardRef(({
  apiKey,
  className,
  geolocation,
  options,
  onClient,
  onOpen,
  onClose,
  onResults,
  onPick,
  onError,
  onCountryChange,
  onDirty,
  onEmpty,
  onFreeForm,
  onGeolocation,
  onCountryMode,
  onState,
  ...inputProps
}, ref) => {
  const {
    target,
    client,
    state
  } = usePlaceKit(apiKey, {
    ...options,
    handlers: {
      onOpen,
      onClose,
      onResults,
      onPick,
      onError,
      onCountryChange,
      onDirty,
      onEmpty,
      onFreeForm,
      onGeolocation,
      onCountryMode,
      onState
    }
  });

  // update default value (only if untouched)
  useEffect(() => {
    if (client && !client.state.dirty) {
      client.setValue(inputProps.defaultValue);
    }
  }, [inputProps.defaultValue, client]);

  // forward ref from `target`
  useEffect(() => {
    if (target.current && ref) {
      if (typeof ref === 'function') {
        ref(target.current);
      } else {
        ref.current = target.current;
      }
    }
  }, [target.current]);

  // pass client to `onClient` when it updates
  useEffect(() => {
    if (onClient?.call) {
      onClient(client);
    }
  }, [client, onClient]);

  // toggle geolocation
  const toggleGeolocation = useCallback(() => {
    if (client) {
      if (client.state.geolocation) {
        client.clearGeolocation();
      } else {
        client.requestGeolocation();
      }
    }
  }, [client]);
  return /*#__PURE__*/React.createElement("div", {
    className: ['pka-input', className].filter(c => c).join(' ')
  }, !!geolocation && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: ['pka-input-geolocation', state.geolocation ? 'pka-enabled' : ''].join(' ').trim(),
    title: "Activate geolocation",
    role: "switch",
    "aria-checked": state.geolocation,
    onClick: toggleGeolocation,
    disabled: inputProps.disabled
  }, /*#__PURE__*/React.createElement("span", {
    className: "pka-sr-only"
  }, "Activate geolocation")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "pka-input-clear",
    title: "Clear value",
    "aria-hidden": state.empty,
    onClick: client?.clear,
    disabled: inputProps.disabled
  }, /*#__PURE__*/React.createElement("span", {
    className: "pka-sr-only"
  }, "Clear value")), /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    type: "search",
    ref: target
  })));
});
PlaceKit.defaultProps = {
  geolocation: true,
  options: {},
  placeholder: 'Search places...'
};
PlaceKit.propTypes = {
  // component options
  geolocation: PropTypes.bool,
  className: PropTypes.string,
  // PlaceKit Autocomplete JS options
  apiKey: PropTypes.string.isRequired,
  options: PropTypes.shape({
    panel: PropTypes.shape({
      className: PropTypes.string,
      offset: PropTypes.number,
      strategy: PropTypes.oneOf(['absolute', 'fixed']),
      flip: PropTypes.bool
    }),
    format: PropTypes.shape({
      flag: PropTypes.func,
      icon: PropTypes.func,
      sub: PropTypes.func,
      noResults: PropTypes.func,
      applySuggestion: PropTypes.string,
      cancel: PropTypes.string
    }),
    countryAutoFill: PropTypes.bool,
    countrySelect: PropTypes.bool,
    timeout: PropTypes.number,
    maxResults: PropTypes.number,
    types: PropTypes.arrayOf(PropTypes.string),
    language: PropTypes.string,
    countries: PropTypes.arrayOf(PropTypes.string),
    coordinates: PropTypes.string
  }),
  // event handlers
  onClient: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onResults: PropTypes.func,
  onPick: PropTypes.func,
  onError: PropTypes.func,
  onDirty: PropTypes.func,
  onEmpty: PropTypes.func,
  onFreeForm: PropTypes.func,
  onGeolocation: PropTypes.func,
  onCountryMode: PropTypes.func,
  onState: PropTypes.func,
  onCountryChange: PropTypes.func

  // other HTML input props get forwarded
};

export { PlaceKit, usePlaceKit };
